import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { DataService } from '../../data.service';
import { NEGYEDEV_JEL, NEM_IRTA_JEL, negyedevesE, nemIrtaE, stornoE, strukturaltRovid } from '../../ertekeles-modositas';

/**
 *  A TANULÓ „ELLENŐRZŐJE” – a tanulói adatlap Ellenőrző fülén, a papír ellenőrző mintájára
 *  MINDEN, ami a tanuló iskolai előmeneteléhez tartozik, egy kiválasztott tanévre:
 *  osztály és csoportok, heti órarend, jegyek (évközi, félévi, év végi, magatartás/szorgalom,
 *  átlag), bejelentett számonkérések, mulasztások és késések, feljegyzések és órai
 *  bejegyzések, közösségi szolgálat, felmentések, záradékok – és ugyanez PDF-ben.
 *
 *  LUSTA BETÖLTÉS: a szülő-komponens csak a fül első megnyitásakor teszi a lapra, ezért az
 *  adatlap megnyitása nem indít tucatnyi kérést.
 *
 *  SZAKASZONKÉNT FÜGGETLEN: minden szakasz a saját végpontjából jön, és ha egy elbukik, csak
 *  annak a helyén áll hibaüzenet – a többi szakasz és a PDF attól még elkészül.
 *
 *  A VÉGPONTOK a családi felületekéi (gondviselő-jegyek, gondviselői órarend, tanulói
 *  számonkérés, hiányzás-igazolás, késés, IKSZ, záradék): ezek a csaladi-hozzaferes.js
 *  szabálya szerint az intézmény munkatársát is átengedik, a gondviselő helyén a tanuló saját
 *  azonosítójával (az SQL „a tanuló saját sora” ága). A feljegyzéseknek saját, csak
 *  munkatársnak szóló végpontja van (feljegyzesek.js › /tanulo/:oktazon), mert a családi
 *  folyam csak a folyó tanévet és csak a családnak szánt típusokat adja.
 */

/** `jel`: a legutóbbi kérés jele – tanév- vagy hétváltáskor a későn érkező régi válasz nem írhatja felül az újat. */
interface Szakasz<T = any> { fut: boolean; hiba: string; adat: T | null; jel?: object; }
const ujSzakasz = <T = any>(): Szakasz<T> => ({ fut: false, hiba: '', adat: null });

/** Az igazolás típusai a `mulasztas_tipus` törzs szerint (hianyzas-igazolas.js › TIPUS_NEVEK). */
const IGAZOLAS_NEV: Record<number, string> = {
  1: 'Orvosi igazolás', 2: 'Gondviselői igazolás', 3: 'Iskolaérdekű távollét', 4: 'Hivatalos távollét',
  5: 'Kikérő / Igazgatói engedély', 6: 'Egyéb (nem keretes)', 7: 'Pályaválasztási célú távollét',
};
const NAPOK = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];

@Component({
  selector: 'app-tanulo-ellenorzo',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './tanulo-ellenorzo.component.html',
  styleUrls: ['./tanulo-ellenorzo.component.scss'],
})
export class TanuloEllenorzoComponent implements OnChanges {
  @Input() oktazon = '';
  @Input() intezmeny = '';
  @Input() nev = '';

  readonly NAPOK = NAPOK;
  tanevLista: { id: number; cimke: string; tankezd: string | null; tanvege: string | null; felev: string | null }[] = [];
  tanev: number | null = null;
  private tizedes = 1;
  private elokeszitve = false;
  pdfFut = false;

  tanugy = ujSzakasz();
  orarend = ujSzakasz<any[]>();
  jegyek = ujSzakasz();
  szamonkeres = ujSzakasz<any[]>();
  mulasztas = ujSzakasz();
  keses = ujSzakasz();
  feljegyzes = ujSzakasz();
  iksz = ujSzakasz();
  zaradek = ujSzakasz<any[]>();

  /** Az órarend hete (hétfő). */
  het = moment().startOf('isoWeek');
  /** Minden futó betöltés – a PDF ezek végét várja meg. */
  private futok: Promise<any>[] = [];

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnChanges(ch: SimpleChanges): Promise<void> {
    if (!(ch['oktazon'] || ch['intezmeny'])) return;
    if (!this.elokeszitve) await this.elokeszit();
    this.mindet();
  }

  /* ── Tanév ──────────────────────────────────────────────────────────────── */

  private get int(): string { return String(this.intezmeny || '').replace(/^\/+|\/+$/g, ''); }
  private get okt(): string { return encodeURIComponent(String(this.oktazon || '')); }

  /** A tanévlista a bejelentkezéskor tárolt `tanevadatok`-ból; alapból az intézmény aktuális tanéve. */
  private async elokeszit(): Promise<void> {
    this.elokeszitve = true;
    try {
      const nyers = await this.data.getFromStorage('tanevadatok');
      const lista = (typeof nyers === 'string' ? JSON.parse(nyers || '[]') : nyers) || [];
      const nap = (v: any) => (v && moment(v).isValid() ? moment(v).format('YYYY-MM-DD') : null);
      this.tanevLista = (Array.isArray(lista) ? lista : [])
        .filter((t: any) => Number.isFinite(Number(t?.id)))
        .map((t: any) => ({
          id: Number(t.id), cimke: t.name || `${Number(t.id)}/${Number(t.id) + 1}`,
          tankezd: nap(t.tankezd), tanvege: nap(t.tanvege), felev: nap(t.felev),
        }))
        .sort((a, b) => b.id - a.id);
    } catch { this.tanevLista = []; }
    const aktualis = await this.data.getAktualisTanev().catch(() => null);
    const most = moment();
    this.tanev = aktualis ?? (most.month() >= 8 ? most.year() : most.year() - 1);
    if (!this.tanevLista.some((t) => t.id === this.tanev)) {
      this.tanevLista.unshift({ id: this.tanev, cimke: `${this.tanev}/${this.tanev + 1}`, tankezd: null, tanvege: null, felev: null });
    }
    try {
      const si = await this.data.getFromStorage('sajatIntezmeny');
      const t = Number(si?.ertekelesAtlagTizedes ?? si?.ertekeles_atlag_tizedes ?? 1);
      this.tizedes = Number.isFinite(t) ? Math.min(3, Math.max(0, Math.trunc(t))) : 1;
    } catch { /* marad az alapérték */ }
    this.hetIgazitas();
  }

  get tanevSor() { return this.tanevLista.find((t) => t.id === this.tanev) || null; }
  /** A tanév határai: a tanév-tábla szerint, vagy szeptember 1. – augusztus 31. */
  get tanevTol(): string { return this.tanevSor?.tankezd || `${this.tanev}-09-01`; }
  get tanevIg(): string { return `${Number(this.tanev) + 1}-08-31`; }
  get tanevCimke(): string { return this.tanevSor?.cimke || `${this.tanev}/${Number(this.tanev) + 1}`; }

  /** Az órarend a mai hetet mutatja; ha a választott tanév nem a mostani, annak az első hetét. */
  private hetIgazitas(): void {
    const ma = moment();
    const tol = moment(this.tanevTol);
    const ig = moment(this.tanevSor?.tanvege || this.tanevIg);
    const cel = ma.isBefore(tol, 'day') || ma.isAfter(ig, 'day') ? tol : ma;
    this.het = cel.clone().startOf('isoWeek');
  }

  tanevValt(): void {
    this.hetIgazitas();
    this.mindet();
  }

  /* ── Betöltés ───────────────────────────────────────────────────────────── */

  /** Egy szakasz betöltése: saját hibával, a többitől függetlenül. */
  private tolt<T>(sz: Szakasz<T>, fn: () => Promise<T>, hibaSzoveg: string): Promise<void> {
    const jel = {};
    sz.jel = jel; sz.fut = true; sz.hiba = ''; sz.adat = null;
    const p = fn()
      .then((r) => { if (sz.jel === jel) sz.adat = r; })
      .catch((e: any) => {
        if (sz.jel !== jel) return;
        sz.hiba = e?.status === 403 ? 'Ehhez a szakaszhoz nincs jogosultságod.'
          : (e?.error?.error && typeof e.error.error === 'string' ? e.error.error : hibaSzoveg);
      })
      .finally(() => { if (sz.jel === jel) sz.fut = false; });
    this.futok.push(p);
    return p;
  }

  private get<T = any>(url: string): Promise<T> { return firstValueFrom(this.http.get<T>(url)); }

  mindet(): void {
    this.futok = [];
    if (!this.oktazon || !this.int || this.tanev == null) return;
    const ev = this.tanev;
    const int = this.int;
    const okt = this.okt;
    this.tolt(this.tanugy, () => this.get(`/api/nyilvantartas/tanulo-tanugy/${okt}/${int}`), 'Az osztály- és csoportadatok most nem tölthetők be.');
    this.orarendBetolt();
    //  A gondviselő helyén a tanuló saját azonosítója: munkatársként ez a végpontok „saját sor” ága.
    this.tolt(this.jegyek, () => this.get(`/api/nyilvantartas/gondviselo-jegyek/${int}/${okt}/${okt}?ev=${ev}`), 'A jegyek most nem tölthetők be.');
    this.tolt(this.szamonkeres, () => this.get<any[]>(`/api/szamonkeres-terv/tanulo/${okt}/${okt}/${int}?ev=${ev}`)
      .then((r) => (Array.isArray(r) ? r : [])), 'A bejelentett számonkérések most nem tölthetők be.');
    this.tolt(this.mulasztas, () => this.get(`/api/hianyzas-igazolas/tanulo/${okt}/${int}?ev=${ev}`), 'A mulasztások most nem tölthetők be.');
    this.tolt(this.keses, () => this.get(`/api/keses/tanulo/${okt}/${int}?tanev=${ev}`), 'A késések most nem tölthetők be.');
    this.tolt(this.feljegyzes, () => this.get(`/api/feljegyzesek/tanulo/${okt}/${int}?ev=${ev}`), 'A feljegyzések most nem tölthetők be.');
    this.tolt(this.iksz, () => this.get(`/api/iksz/tanulo/${okt}/${int}`), 'A közösségi szolgálat adatai most nem tölthetők be.');
    this.tolt(this.zaradek, () => this.get<any[]>(`/api/zaradek/${okt}?intezmeny=${encodeURIComponent(int)}`)
      .then((r) => (Array.isArray(r) ? r : [])), 'A záradékok most nem tölthetők be.');
  }

  orarendBetolt(): Promise<void> {
    const ev = this.het.isoWeekYear();
    const hetSzam = this.het.isoWeek();
    return this.tolt(this.orarend, () => this.get<any>(`/api/orarend/gondviselo/${this.okt}/${this.okt}/${ev}/${hetSzam}/${this.int}`)
      .then((r) => (Array.isArray(r?.orak) ? r.orak : [])), 'Az órarend most nem tölthető be.');
  }

  hetLeptet(irany: number): void {
    this.het = irany === 0 ? moment().startOf('isoWeek') : this.het.clone().add(irany, 'week');
    this.orarendBetolt();
  }

  get hetCimke(): string {
    const veg = this.het.clone().add(4, 'day');
    return `${this.het.format('YYYY.MM.DD.')} – ${veg.format('MM.DD.')} (${this.het.isoWeek()}. hét)`;
  }

  /* ── Osztály, csoportok, felmentések ────────────────────────────────────── */

  private atfed(kezdete: any, vege: any): boolean {
    const k = String(kezdete || '');
    const v = String(vege || '');
    const nyitott = !v || v.startsWith('0000');
    return (!k || k <= this.tanevIg) && (nyitott || v >= this.tanevTol);
  }

  vegeSzoveg(v: any): string { return !v || String(v).startsWith('0000') ? 'folyamatban' : String(v); }

  get jogviszonyok(): any[] {
    return (this.tanugy.adat?.jogviszony || []).filter((j: any) => this.atfed(j.kezdete, j.vege));
  }
  get csoportok(): any[] {
    return (this.tanugy.adat?.csoportok || []).filter((c: any) => this.atfed(c.kezdete, c.vege));
  }
  get felmentesek(): any[] {
    return (this.tanugy.adat?.felmentesek || []).filter((f: any) => this.atfed(f.kezdete, f.vege));
  }
  felmentesMiAlol(f: any): string {
    const r: string[] = [];
    if (+f?.oralatogatas_aloli) r.push('óralátogatás');
    if (+f?.ertekeles_aloli) r.push('értékelés');
    if (+f?.szovegesen_ertekelheto) r.push('szövegesen értékelhető');
    return r.length ? r.join(', ') : 'kedvezmény';
  }
  get osztalyNev(): string {
    const elo = this.jogviszonyok.find((j: any) => !j.vege || String(j.vege).startsWith('0000')) || this.jogviszonyok[0];
    return elo?.osztaly_nev || this.jegyek.adat?.tanulo?.osztaly || '';
  }

  /* ── Órarend ────────────────────────────────────────────────────────────── */

  get hetNapjai(): number[] {
    const szombat = (this.orarend.adat || []).some((o: any) => moment(o.datum).isoWeekday() === 6);
    return szombat ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4];
  }
  get oraSorok(): number[] {
    const szamok = (this.orarend.adat || []).map((o: any) => Number(o.ora)).filter((n: number) => Number.isFinite(n));
    if (!szamok.length) return [];
    const min = Math.min(...szamok);
    const max = Math.max(...szamok);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }
  napDatum(nap: number): string { return this.het.clone().add(nap, 'day').format('MM.DD.'); }
  cellaOrak(nap: number, ora: number): any[] {
    const datum = this.het.clone().add(nap, 'day').format('YYYY-MM-DD');
    return (this.orarend.adat || []).filter((o: any) => moment(o.datum).format('YYYY-MM-DD') === datum && Number(o.ora) === ora);
  }
  elmaradtE(o: any): boolean { return Number(o?.elmaradt) === 1; }
  oraSzoveg(o: any): string {
    const tanar = o.helyettesito ? `${o.helyettesito} (helyettesít)` : (o.tarto || '');
    return [o.tantargy || o.tantargy_nev || '–', o.terem || '', tanar, this.elmaradtE(o) ? 'ELMARAD' : '',
      o.sajat?.hianyzott ? 'hiányzott' : ''].filter(Boolean).join(' · ');
  }

  /* ── Jegyek ─────────────────────────────────────────────────────────────── */

  private magSzorgE(t: any): boolean { return /magatart|szorgalom/i.test(String(t?.tantargy_nev || '')); }

  get tantargyak(): any[] {
    return (this.jegyek.adat?.tantargyak || []).filter((t: any) => !this.magSzorgE(t));
  }
  get magatartasSzorgalom(): any[] {
    return (this.jegyek.adat?.tantargyak || []).filter((t: any) => this.magSzorgE(t));
  }
  get jegyesTantargyDb(): number {
    return this.tantargyak.filter((t: any) => (t.ertekelesek || []).length).length;
  }

  private modja(j: any): string { return String(j?.modja ?? '').trim(); }
  zaroE(j: any): boolean { return ['2', '3'].includes(this.modja(j)); }

  /** A félév határa: a tanév-tábla `felev` dátuma, vagy január 19. */
  private get felevHatar(): string { return this.tanevSor?.felev || `${Number(this.tanev) + 1}-01-19`; }
  private nap(v: any): string { return v && moment(v).isValid() ? moment(v).format('YYYY-MM-DD') : ''; }

  /** Évközi jegyek időrendben, félévenként (a negyedéves is ide kerül, jelölve). */
  evkozi(t: any, felev: 1 | 2): any[] {
    return (t?.ertekelesek || [])
      .filter((j: any) => !this.zaroE(j))
      .filter((j: any) => (felev === 1 ? this.nap(j.datum) <= this.felevHatar : this.nap(j.datum) > this.felevHatar))
      .slice().sort((a: any, b: any) => this.nap(a.datum).localeCompare(this.nap(b.datum)));
  }
  zaro(t: any, modja: '2' | '3'): any[] {
    return (t?.ertekelesek || []).filter((j: any) => this.modja(j) === modja && !stornoE(j));
  }

  stornoE = stornoE;
  nemIrtaE = nemIrtaE;

  jegyRovid(j: any): string {
    if (nemIrtaE(j)) return NEM_IRTA_JEL;
    const s = strukturaltRovid(j);
    if (s) return s;
    const v = String(j?.jegy ?? '').trim();
    const fokozat: Record<string, string> = { 'Példás': 'P', 'Jó': 'J', 'Változó': 'V', 'Rossz': 'R', 'Hanyag': 'H' };
    if (fokozat[v]) return fokozat[v];
    if (/^-?\d+([.,]\d+)?\s*%?$/.test(v)) return v.replace(/\s+/g, '');
    //  Hosszú szöveges értékelés: a végén álló fokozat (pl. „…Példás”), különben „SZ”.
    const vege = Object.keys(fokozat).find((k) => v.endsWith(k));
    if (v.length > 4) return vege ? fokozat[vege] : 'SZ';
    return v || '–';
  }
  /** N1–N4 a negyedéves értékelésnél, különben null (a színes téglalap jele, global.scss). */
  negyedevJel(j: any): string | null {
    return NEGYEDEV_JEL[this.modja(j)] || null;
  }
  jegyCim(j: any): string {
    return [this.nap(j.datum), j.emnev || j.ertnev || '', j.tema || '', j.tanar_nev || '',
      NEGYEDEV_JEL[this.modja(j)] ? 'negyedéves' : '', stornoE(j) ? 'stornózva' : '',
      String(j?.jegy ?? '').length > 4 ? String(j.jegy) : ''].filter(Boolean).join(' · ');
  }
  jegySzin(j: any): string {
    if (stornoE(j) || nemIrtaE(j)) return 'medium';
    const n = this.szam(j?.jegy);
    if (n == null || n > 5) return 'dark';
    return n >= 5 ? 'success' : n >= 4 ? 'primary' : n >= 3 ? 'warning' : 'danger';
  }
  private szam(v: any): number | null {
    const m = String(v ?? '').trim().match(/^-?\d+(?:[.,]\d+)?/);
    if (!m) return null;
    const n = Number(m[0].replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  /** Súlyozott átlag – a szülői nézettel azonos szabály: a záró, a negyedéves, a „nem írta” és a stornózott kimarad, a 0 súly nem számít. */
  atlag(t: any): string {
    const tetelek = (t?.ertekelesek || [])
      .filter((j: any) => !this.zaroE(j) && !negyedevesE(j) && !nemIrtaE(j) && !stornoE(j))
      .map((j: any) => {
        const nyers = j?.szorzo;
        let suly = nyers === undefined || nyers === null || nyers === '' ? 1 : Number(String(nyers).replace(',', '.').replace('%', ''));
        if (!Number.isFinite(suly) || suly < 0) suly = 1;
        if (suly > 10) suly = suly / 100;
        return { v: this.szam(j?.jegy), s: suly };
      })
      .filter((x: any) => x.v != null && x.s > 0);
    if (!tetelek.length) return '–';
    const atl = tetelek.reduce((n: number, x: any) => n + x.v * x.s, 0) / tetelek.reduce((n: number, x: any) => n + x.s, 0);
    const szazalek = (t?.ertekelesek || []).some((j: any) => j?.ertek_forma === 'szazalek' || /%\s*$/.test(String(j?.jegy ?? '')));
    return `${atl.toFixed(this.tizedes).replace('.', ',')}${szazalek ? '%' : ''}`;
  }

  /* ── Számonkérések ──────────────────────────────────────────────────────── */

  readonly ma = moment().format('YYYY-MM-DD');

  /* ── Mulasztások ────────────────────────────────────────────────────────── */

  get mulasztasNapok(): any[] { return this.mulasztas.adat?.napok || []; }
  /** Összesítő: napok, órák, igazolt / igazolandó / igazolatlan, és típusonként. */
  get mulasztasOsszesito() {
    const ki = { nap: 0, ora: 0, igazolt: 0, igazolando: 0, igazolatlan: 0, tipusok: [] as { nev: string; db: number }[] };
    const tipusDb = new Map<string, number>();
    for (const n of this.mulasztasNapok) {
      ki.nap += 1;
      for (const o of n.orak || []) {
        ki.ora += 1;
        const t = Number(o.igazolas_tipus);
        if (t > 0) {
          ki.igazolt += 1;
          const nev = IGAZOLAS_NEV[t] || `Igazolt (${t})`;
          tipusDb.set(nev, (tipusDb.get(nev) || 0) + 1);
        } else if (t <= -2) ki.igazolatlan += 1;
        else ki.igazolando += 1;
      }
    }
    ki.tipusok = [...tipusDb.entries()].map(([nev, db]) => ({ nev, db })).sort((a, b) => b.db - a.db);
    return ki;
  }
  igazolasNev(t: any): string {
    const n = Number(t);
    if (n > 0) return IGAZOLAS_NEV[n] || 'igazolt';
    return n <= -2 ? 'igazolatlan' : 'igazolandó';
  }
  igazolasSzin(t: any): string {
    const n = Number(t);
    return n > 0 ? 'success' : n <= -2 ? 'danger' : 'warning';
  }
  napOrak(n: any): string {
    return (n?.orak || []).slice().sort((a: any, b: any) => a.ora - b.ora)
      .map((o: any) => `${o.ora}. ${o.tantargy || ''} (${this.igazolasNev(o.igazolas_tipus)})`).join(', ');
  }

  /* ── Feljegyzések ───────────────────────────────────────────────────────── */

  get feljegyzesek(): any[] { return this.feljegyzes.adat?.feljegyzesek || []; }
  get oraiBejegyzesek(): any[] { return this.feljegyzes.adat?.orai || []; }
  /** Típusonkénti darabszám (dicséret, figyelmeztetés, intő, házi feladat hiány…). */
  get feljegyzesOsszesito(): { nev: string; db: number }[] {
    const m = new Map<string, number>();
    for (const f of [...this.feljegyzesek, ...this.oraiBejegyzesek]) m.set(f.tipus || 'Egyéb', (m.get(f.tipus || 'Egyéb') || 0) + 1);
    return [...m.entries()].map(([nev, db]) => ({ nev, db })).sort((a, b) => b.db - a.db);
  }
  feljegyzesSzin(tipus: any): string {
    const t = String(tipus || '').toLowerCase();
    if (t.includes('dics')) return 'success';
    if (t.includes('intő') || t.includes('into') || t.includes('megrov')) return 'danger';
    if (t.includes('figyelmezt') || t.includes('hiány') || t.includes('hiany')) return 'warning';
    return 'medium';
  }

  /* ── Záradékok ──────────────────────────────────────────────────────────── */

  get zaradekok(): any[] {
    return (this.zaradek.adat || []).filter((z: any) => !z.datum || (z.datum >= this.tanevTol && z.datum <= this.tanevIg));
  }

  /* ── Nyomtatás / PDF ────────────────────────────────────────────────────── */

  /**
   *  Az egész ellenőrző PDF-ben, a kiszolgáló riport-motorjával (backend/pdf-riport.js).
   *  Előbb megvárja a még futó betöltéseket; a hibás szakasz a PDF-ben is hibaüzenettel áll.
   */
  async pdf(): Promise<void> {
    if (this.pdfFut) return;
    this.pdfFut = true;
    await this.data.showLoading('Az ellenőrző PDF-je készül...');
    try {
      //  A `tolt` ígéretei sosem utasítanak el (a hibát a szakaszba írják), így elég az `all`.
      await Promise.all(this.futok);
      const tablak = this.pdfTablak();
      const safe = String(this.nev || this.oktazon || 'tanulo').replace(/[^\p{L}\p{N}]+/gu, '_');
      await this.data.riportPdf({
        intezmeny: this.int,
        cim: this.nev || 'Tanuló',
        cimKieg: 'ellenőrző',
        alcim: `${this.tanevCimke} tanév`,
        adatsav: [
          ['Tanuló', this.nev || ''], ['Oktatási azonosító', String(this.oktazon || '')],
          ['Osztály', this.osztalyNev || '–'], ['Tanév', this.tanevCimke],
        ],
        lapmeret: 'A4', tajolas: 'allo', betumeret: 8.5,
        tablak,
        lablec: { kelt: true },
        fajlnev: `${safe}_ellenorzo_${this.tanev}.pdf`,
      //  Letöltés, nem új lap: a betöltések kivárása után a kattintás már nem „friss”, a
      //  böngésző az új lapot letiltaná.
      }, `${safe}_ellenorzo_${this.tanev}.pdf`, 'letolt');
    } catch (e: any) {
      this.data.sendToast(e?.message || 'A PDF elkészítése nem sikerült.', 'error');
    } finally {
      this.pdfFut = false;
      await this.data.hideLoading();
    }
  }

  /** Egy szakasz táblája; ha a szakasz elbukott, a hibaüzenet áll a sorok helyén. */
  private pdfTabla(sz: Szakasz, cim: string, oszlopok: any[], sorok: () => any[], ures: string, osszegzes = ''): any {
    if (sz.hiba || !sz.adat) {
      return { cim, oszlopok, sorok: [], ures: sz.hiba ? `Nem tölthető be: ${sz.hiba}` : 'Nincs adat.' };
    }
    return { cim, oszlopok, sorok: sorok(), ures, osszegzes };
  }

  private pdfTablak(): any[] {
    const t: any[] = [];
    const osz = this.mulasztasOsszesito;

    t.push(this.pdfTabla(this.tanugy, 'Osztály és csoportok',
      [{ nev: 'Típus', szelesseg: '14%' }, { nev: 'Megnevezés', szelesseg: '46%' }, { nev: 'Kezdete', szelesseg: '20%' }, { nev: 'Vége', szelesseg: '20%' }],
      () => [
        ...this.jogviszonyok.map((j: any) => ['Osztály', { t: `${j.osztaly_nev || '–'}${j.evfolyam ? ` (${j.evfolyam}. évf.)` : ''}`, fej: true }, j.kezdete || '–', this.vegeSzoveg(j.vege)]),
        ...this.csoportok.map((c: any) => ['Csoport', c.csoport || '–', c.kezdete || '–', this.vegeSzoveg(c.vege)]),
      ], 'Nincs osztály- vagy csoporttagság a tanévben.'));

    t.push(this.pdfTabla(this.orarend, `Órarend – ${this.hetCimke}`,
      [{ nev: 'Óra', igazitas: 'kozep', szelesseg: '6%' }, ...this.hetNapjai.map((n) => ({ nev: `${NAPOK[n]} ${this.napDatum(n)}` }))],
      () => this.oraSorok.map((ora) => [{ t: `${ora}.`, fej: true },
        ...this.hetNapjai.map((n) => this.cellaOrak(n, ora).map((o) => this.oraSzoveg(o)).join('\n'))]),
      'Ezen a héten nincs órája.'));

    const jegySor = (tt: any) => [
      { t: tt.tantargy_nev || '–', fej: true },
      this.evkozi(tt, 1).map((j) => this.jegyRovid(j) + (stornoE(j) ? '(storno)' : '')).join(' '),
      this.zaro(tt, '2').map((j) => this.jegyRovid(j)).join(' '),
      this.evkozi(tt, 2).map((j) => this.jegyRovid(j) + (stornoE(j) ? '(storno)' : '')).join(' '),
      this.zaro(tt, '3').map((j) => this.jegyRovid(j)).join(' '),
      this.atlag(tt),
    ];
    t.push(this.pdfTabla(this.jegyek, 'Értékelések',
      [{ nev: 'Tantárgy', szelesseg: '22%' }, { nev: 'I. félév', szelesseg: '25%' }, { nev: 'Félévi', igazitas: 'kozep', szelesseg: '7%' },
        { nev: 'II. félév', szelesseg: '25%' }, { nev: 'Év végi', igazitas: 'kozep', szelesseg: '7%' }, { nev: 'Átlag', igazitas: 'jobb', szelesseg: '8%' }],
      () => [
        ...this.tantargyak.filter((tt: any) => (tt.ertekelesek || []).length).map(jegySor),
        ...(this.magatartasSzorgalom.length ? [{ csoport: 'Magatartás és szorgalom' }, ...this.magatartasSzorgalom.map(jegySor)] : []),
      ], 'Nincs értékelés a tanévben.', `${this.jegyesTantargyDb} tantárgy`));

    t.push(this.pdfTabla(this.szamonkeres, 'Bejelentett számonkérések',
      [{ nev: 'Dátum', szelesseg: '13%' }, { nev: 'Tantárgy', szelesseg: '22%' }, { nev: 'Típus', szelesseg: '20%' }, { nev: 'Téma', szelesseg: '45%' }],
      () => (this.szamonkeres.adat || []).map((s: any) => [s.datum, s.tantargy_nev || '–', s.tipus_nev || '', [s.cim, s.leiras].filter(Boolean).join(' – ')]),
      'Nincs bejelentett számonkérés.'));

    t.push(this.pdfTabla(this.mulasztas, 'Mulasztások – összesítő',
      [{ nev: 'Napok' }, { nev: 'Órák' }, { nev: 'Igazolt' }, { nev: 'Igazolandó' }, { nev: 'Igazolatlan' }],
      () => [
        [String(osz.nap), String(osz.ora), String(osz.igazolt), String(osz.igazolando), String(osz.igazolatlan)],
        ...osz.tipusok.map((x) => [{ t: x.nev, cs: 4, halvany: true }, `${x.db} óra`]),
      ], 'Nincs mulasztás.'));
    if (this.mulasztasNapok.length) {
      t.push({
        cim: 'Mulasztások napokra bontva',
        oszlopok: [{ nev: 'Dátum', szelesseg: '13%' }, { nev: 'Órák', igazitas: 'kozep', szelesseg: '7%' }, { nev: 'Órák és igazolásuk', szelesseg: '80%' }],
        sorok: this.mulasztasNapok.map((n: any) => [n.datum, String(n.ora_db ?? (n.orak || []).length), this.napOrak(n)]),
      });
    }

    t.push(this.pdfTabla(this.keses, 'Késések',
      [{ nev: 'Dátum', szelesseg: '13%' }, { nev: 'Óra', igazitas: 'kozep', szelesseg: '7%' }, { nev: 'Tantárgy', szelesseg: '30%' },
        { nev: 'Perc', igazitas: 'jobb', szelesseg: '8%' }, { nev: 'Igazolás', szelesseg: '42%' }],
      () => (this.keses.adat?.tetelek || []).map((k: any) => [this.nap(k.datum), k.oraszam != null ? `${k.oraszam}.` : '', k.tantargy_nev || '',
        `${k.perc} perc`, +k.igazolt ? `igazolt${k.indok ? ` – ${k.indok}` : ''}` : 'igazolatlan']),
      'Nincs késés.', this.keses.adat ? `igazolatlan ${this.keses.adat.perc_igazolatlan || 0} perc, igazolt ${this.keses.adat.perc_igazolt || 0} perc` : ''));

    t.push(this.pdfTabla(this.feljegyzes, 'Feljegyzések, beírások',
      [{ nev: 'Dátum', szelesseg: '12%' }, { nev: 'Típus', szelesseg: '24%' }, { nev: 'Szöveg', szelesseg: '46%' }, { nev: 'Rögzítette', szelesseg: '18%' }],
      () => this.feljegyzesek.map((f: any) => [f.datum, { t: f.tipus || '', fej: true }, [f.cim, f.szoveg].filter(Boolean).join(' – '), f.tanar_nev || '']),
      'Nincs feljegyzés.', `${this.feljegyzesek.length} db`));
    t.push(this.pdfTabla(this.feljegyzes, 'Órai bejegyzések (házi feladat, felszerelés, dicséret)',
      [{ nev: 'Dátum', szelesseg: '12%' }, { nev: 'Óra', igazitas: 'kozep', szelesseg: '6%' }, { nev: 'Tantárgy', szelesseg: '30%' }, { nev: 'Bejegyzés', szelesseg: '24%' }, { nev: 'Tanár', szelesseg: '28%' }],
      () => this.oraiBejegyzesek.map((o: any) => [o.datum, o.ora != null ? `${o.ora}.` : '', o.tantargy_nev || '', { t: o.tipus, fej: true }, o.tanar_nev || '']),
      'Nincs órai bejegyzés.', `${this.oraiBejegyzesek.length} db`));

    t.push(this.pdfTabla(this.iksz, 'Közösségi szolgálat',
      [{ nev: 'Időszak', szelesseg: '24%' }, { nev: 'Tevékenység', szelesseg: '34%' }, { nev: 'Hely', szelesseg: '30%' }, { nev: 'Óra', igazitas: 'jobb', szelesseg: '12%' }],
      () => (this.iksz.adat?.tetelek || []).map((x: any) => [`${x.kezdete || ''} – ${x.vege || ''}`, x.tipus || '', x.teljesitesi_hely || '', `${x.oraszam} óra`]),
      'Nincs rögzített közösségi szolgálat.', this.iksz.adat ? `${this.iksz.adat.ora} / ${this.iksz.adat.cel} óra` : ''));

    t.push(this.pdfTabla(this.tanugy, 'Felmentések',
      [{ nev: 'Tantárgy', szelesseg: '24%' }, { nev: 'Időszak', szelesseg: '22%' }, { nev: 'Mi alól', szelesseg: '24%' }, { nev: 'Ok / határozat', szelesseg: '30%' }],
      () => this.felmentesek.map((f: any) => [{ t: f.tantargy_nev || '–', fej: true }, `${f.kezdete || '?'} – ${f.vege || 'visszavonásig'}`,
        this.felmentesMiAlol(f), [f.ok, f.hatarozat].filter(Boolean).join(' – ')]),
      'Nincs felmentés a tanévben.'));

    t.push(this.pdfTabla(this.zaradek, 'Záradékok',
      [{ nev: 'Dátum', szelesseg: '13%' }, { nev: 'Kód', szelesseg: '9%' }, { nev: 'Szöveg', szelesseg: '78%' }],
      () => this.zaradekok.map((z: any) => [z.datum || '', z.melleklet_kod || '', z.szoveg || '']),
      'Nincs záradék a tanévben.'));
    return t;
  }
}
